import { Injectable, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  public isMobile$ = new BehaviorSubject<boolean>(false);
  public sidebarOpen$ = new BehaviorSubject<boolean>(true);
  public viewMode$ = new BehaviorSubject<'table' | 'card'>(this.readInitialViewMode());

  readonly isMobile = toSignal(this.isMobile$, { initialValue: false });
  readonly sidebarOpen = toSignal(this.sidebarOpen$, { initialValue: true });
  readonly viewMode = toSignal(this.viewMode$, { initialValue: 'table' as 'table' | 'card' });
  readonly isCardView = computed(() => this.viewMode() === 'card');

  constructor(public http: HttpService) { }

  private readInitialViewMode(): 'table' | 'card' {
    try {
      const raw = localStorage.getItem('appConfig');
      if (raw) {
        const cfg = JSON.parse(raw);
        if (cfg && (cfg.viewMode === 'table' || cfg.viewMode === 'card')) {
          return cfg.viewMode;
        }
      }
    } catch (e) {}
    return 'table';
  }

  setViewMode(mode: 'table' | 'card'): void {
    try {
      const raw = localStorage.getItem('appConfig');
      const cfg = raw ? JSON.parse(raw) : {};
      cfg.viewMode = mode;
      localStorage.setItem('appConfig', JSON.stringify(cfg));
    } catch (e) {}
    this.viewMode$.next(mode);
  }

  apiBaseUrl = environment.apiBaseUrl;

  getDashboardData(): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/dp/dashboard`);
  }

  getMyStoreRatingAndReviews(): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/dp/review-list`);
  }

  getReviewDetails(reviewId: string | number): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/dp/review-details?id=${reviewId}`);
  }
  
  getBanners(): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/get-contractor-banner`);
  }

  getPolicies(user_type:string, page:string): Observable<any>{
    return this.http.get(`${environment.apiBaseUrl}/get-page?user_type=${user_type}&page=${page}`);
  }

  getFaqs(user_type:string, type?:string): Observable<any>{
    let url = `${environment.apiBaseUrl}/get-faqs?user_type=${user_type}`;
    if (type) url += `&type=${type}`;
    return this.http.get(url);
  }

  getDocuments(employeeId?: number):Observable<any> {
    if(employeeId) {
      let params = new HttpParams().set('id', employeeId);
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-documents-list`, {params: params})
    }else {
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-documents-list`);
    }
  } 

  getCities(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/get-cities`);
  }

  getMyReviews(page:string, employeeId?: any): Observable<any> {
    if(employeeId) {
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-review-list?page=${page}&id=${employeeId}`);
    } else {
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-review-list?page=${page}`);
    }
  }

  getApplicationConfig(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/config-settings`);
  }

  getPolicyDetail(policyUrl: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/${policyUrl}`);
  }

  getCountries(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/get-countries`);
  }

  getEarningDashboard(type: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'yearly'): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/contractor/earning-dashboard?type=${type}`);
  }

  getEarningStatement(start_date: string, end_date: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/contractor/earning-statement`, {
      start_date,
      end_date,
    });
  }
}
